import {
  Array,
  Boolean,
  Dictionary,
  Optional,
  Record,
  Static,
  String,
  Union,
} from "runtypes";

import {
  BinaryColumnPredicateOpLiteral,
  ListBinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
} from "./display-table/columnPredicateTypes.js";
import {
  SemanticDatasetName,
  SemanticProjectId,
  StaticCellId,
} from "./idTypeBrands.js";
import { ParameterInputType } from "./parameterType.js";
import { ScopeItemType, ScopeItemTypeLiteral } from "./scopeTypes.js";

export const SharedFilterPredicateOp = Union(
  UnaryColumnPredicateOpLiteral,
  BinaryColumnPredicateOpLiteral,
  ListBinaryColumnPredicateOpLiteral,
);
export type SharedFilterPredicateOp = Static<typeof SharedFilterPredicateOp>;

export const SharedFilterManualLink = Record({
  staticCellId: StaticCellId,
  dataFrame: String,
  dataFrameColumn: String,
  isExclude: Optional(Boolean),
  queryPath: Optional(Array(SemanticDatasetName)),
});
export type SharedFilterManualLink = Static<typeof SharedFilterManualLink>;

// dictionary of base dataset to related datasets
export const SemanticDatasetNameArray = Array(SemanticDatasetName);
export type SemanticDatasetNameArray = Static<typeof SemanticDatasetNameArray>;
export const SemanticRelatedDatasets = Dictionary(
  SemanticDatasetNameArray,
  SemanticDatasetName,
);
export type SemanticRelatedDatasets = Static<typeof SemanticRelatedDatasets>;

export const SharedFilterAutoLink = Record({
  dataFrame: String,
  dataFrameColumn: String,
  dataSourceTableName: Optional(String),
  queryPath: Optional(Array(SemanticDatasetName)),
  // if true, will include cells not in the app (cells only in the logic view)
  // when computing shared filter links
  includeNonAppCells: Boolean,
  // deprecated - see comment below
  scopeItemType: Optional(ScopeItemTypeLiteral),
  semanticDatasetName: Optional(SemanticDatasetName),
  semanticProjectId: Optional(SemanticProjectId),
  /*
   * Maps each dataset in the semantic project to its related datasets (related = joined directly or indirectly).
   * We need this in order to build filter links since auto-link + cell-contents are not sufficient to determine
   * whether an auto-link applies to a given cell (for semantic data).
   *
   * This is populated by SharedFilterColumnPicker and used by sharedFilterLinkUtils().
   */
  semanticRelatedDatasets: Optional(SemanticRelatedDatasets),
});

export type SharedFilterAutoLink = Omit<
  Static<typeof SharedFilterAutoLink>,
  "scopeItemType"
> & {
  // hack - redefining this field here so that we can mark it as deprecated
  /**
   * @deprecated - this is as legacy field and should not be used - we are keeping it around
   * so that old file schemas with this field are still supported/for viz since
   * some entities will have this set.
   */
  scopeItemType?: ScopeItemType;
};

export const SharedFilterAutoLinks = Array(SharedFilterAutoLink);
export type SharedFilterAutoLinks = Static<typeof SharedFilterAutoLinks>;

export const SharedFilterManualLinks = Array(SharedFilterManualLink);
export type SharedFilterManualLinks = Static<typeof SharedFilterManualLinks>;

export type SharedFilterInputType = Exclude<
  ParameterInputType,
  "BUTTON" | "TABLE" | "FILTER" | "FILE_UPLOAD"
>;

export const DataFrameDistinctValues = Dictionary(Dictionary(Array(String)));
export type DataFrameDistinctValues = Static<typeof DataFrameDistinctValues>;
